import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronLeft } from 'lucide-react';

import { useGoBack } from '@muzly/shared';

/** 바가 안전 영역 위로 얹는 여백 — 페이지 루트가 아니라 바가 직접 든다(아래 설명). */
const SAFE_PAD = 'calc(var(--safe-area-top, 0px) + 0.25rem)';

/** 바 본체의 높이(`h-14`)에 위 여백 0.25rem 을 더한 값 — 안전 영역 **아래**로 바가
 *  차지하는 세로 길이. 바 밑에 또 하나를 붙이는 쪽(결과 상세의 지표 탭)이 이 치수를
 *  알아야 겹치지 않으므로 상수 하나로 둔다 — 두 곳에 적으면 한쪽만 고쳐질 자리다. */
const BAR_HEIGHT = '3.75rem';

/** 상단 바 바로 밑에 붙어 서는 요소의 `top` — 안전 영역 + 바 높이. */
export const PAGE_HEADER_STICKY_TOP = `calc(var(--safe-area-top, 0px) + ${BAR_HEIGHT})`;

/**
 * 드릴다운 화면의 상단 바(시안 `top bar`) — 좌 `<` · 가운데 제목(16px Medium) · 우 부속.
 * 이력·결과 상세·MY 하위 화면이 같은 바를 쓴다. 제목이 가운데라 좌우 폭을 맞춰야
 * 하므로 부속이 없어도 오른쪽 자리는 비워 둔다.
 *
 * 스크롤을 타고 올라가지 않는다(`sticky`) — 탭바를 감추는 드릴다운에서 이 `<` 가 유일한
 * 출구이고(`hideBack` 화면은 우측 X 가 그 출구다), 제목은 여러 문서 중 어디에 있는지 말하는 유일한 표시다. 안전 영역 패딩을
 * **바가 직접 들고 있어야** 한다: 페이지 루트에 두면 바는 스크롤 컨테이너의 y=0(노치
 * 밑)에 붙고 그 위에 투명한 띠가 남는다. 그래서 이 바를 쓰는 화면의 루트에는
 * `--safe-area-top` 패딩이 없다.
 *
 * `z-30` 은 상태바 뒤판(`app.tsx`, z-40) **아래**다 — 둘 다 페이지 배경색을 칠하므로
 * 뒤판이 바의 안전 영역 띠를 같은 색으로 덮는다.
 */
export const PageHeader = ({
  title,
  right,
  onBack,
  hideBack = false,
}: {
  title: ReactNode;
  right?: ReactNode;
  /** 없으면 히스토리 뒤로가기. `hideBack` 이면 쓰이지 않는다. */
  onBack?: () => void;
  /** 좌측 `<` 를 뺀다 — 나가는 길이 우측 X 하나인 화면(의견 남기기). 자리는 남긴다. */
  hideBack?: boolean;
}) => {
  const { t } = useTranslation();
  const goBack = useGoBack();
  return (
    <header
      className="sticky top-0 z-30 flex items-center justify-between bg-background px-2"
      // `box-sizing: border-box` 라 `min-h-14` 만 쓰면 안전 영역 패딩이 바 본체 높이를
      // 잡아먹는다 — 최소 높이에 패딩을 더해 본체가 늘 `h-14` 를 지키게 한다.
      style={{
        paddingTop: SAFE_PAD,
        minHeight: `calc(3.5rem + ${SAFE_PAD})`,
      }}
    >
      {hideBack ? (
        <div className="h-11 w-11" aria-hidden="true" />
      ) : (
        <button
          type="button"
          onClick={() => (onBack ? onBack() : void goBack())}
          aria-label={t('web.common.back')}
          className="flex h-11 w-11 items-center justify-center text-bk-900"
        >
          <ChevronLeft className="h-6 w-6" strokeWidth={2} />
        </button>
      )}
      <h1 className="pointer-events-none absolute inset-x-14 truncate text-center text-base font-medium tracking-[-0.16px] text-bk-900">
        {title}
      </h1>
      <div className="flex h-11 min-w-11 items-center justify-center">
        {right}
      </div>
    </header>
  );
};
